import { useCallback, useState } from 'react';

/**
 * チャートの onClick / onMouseLeave が受け取る state のうち、
 * トグル判定に必要なプロパティのみを構造的に定義
 * （recharts の MouseHandlerDataParam と互換）
 */
interface ChartClickState {
  activeTooltipIndex?: number | string | null;
}

/**
 * グラフ上のタップでツールチップ（パネル）表示を ON/OFF するためのフック
 *
 * recharts の Tooltip はタップで表示された後、グラフ上を再タップしても
 * 消えないため、表示状態を自前で管理して Tooltip の active prop に渡す。
 *
 * - 非表示中にタップ → タップ位置のパネルを表示
 * - 表示中に同じデータ点をタップ → パネルを非表示
 * - 表示中に別のデータ点をタップ → パネルをその位置へ移動
 * - グラフ外タップ（mouseleave）→ recharts 内部状態の消灯に合わせてリセット
 */
export const useTooltipToggle = () => {
  // recharts の activeTooltipIndex は number / string の両方を取り得る型のため、
  // 文字列に正規化して保持し、同一データ点の再タップ判定が型差でブレないようにする。
  const [visibleIndex, setVisibleIndex] = useState<string | null>(null);

  const handleChartClick = useCallback((state: ChartClickState) => {
    const index = state?.activeTooltipIndex;
    if (index == null) return;
    const key = String(index);
    setVisibleIndex((prev) => (prev === key ? null : key));
  }, []);

  const handleChartLeave = useCallback(() => {
    setVisibleIndex(null);
  }, []);

  return {
    /** Tooltip の active prop に渡す表示フラグ */
    tooltipActive: visibleIndex !== null,
    /** チャートの onClick に渡すハンドラー */
    handleChartClick,
    /** チャートの onMouseLeave に渡すハンドラー */
    handleChartLeave,
  };
};

export default useTooltipToggle;
